import { FINGERPRINT_EMOJIS } from "./emojiTable";

/**
 * Spec-fixed length of the fingerprint seed in bytes.
 * See plan/docs/export_architecture_v2.md §6.
 */
export const FINGERPRINT_SEED_LENGTH_BYTES = 16;

/**
 * Derives the human-readable fingerprint string shown to the user during an
 * export session, from a 16-byte seed produced by the mobile app.
 *
 * Format: `"${emoji1} ${emoji2} ${digits4}"` — e.g. `"🦊 🌙 4271"`.
 *
 * The Flutter side (`app/lib/features/export/shared/fingerprint_deriver.dart`)
 * implements the same algorithm against the same emoji table, so a matching
 * seed produces a byte-identical string on both ends. Any drift here
 * (byte order, modulo size, padding, separator) silently breaks
 * mobile/PC matching, which is the security primitive itself
 * (see plan/docs/export_security_v1.md §3.3).
 *
 * @throws RangeError if `seed.byteLength !== FINGERPRINT_SEED_LENGTH_BYTES`.
 */
export async function deriveFingerprint(seed: Uint8Array): Promise<string> {
  if (seed.byteLength !== FINGERPRINT_SEED_LENGTH_BYTES) {
    throw new RangeError(
      `seed must be exactly ${FINGERPRINT_SEED_LENGTH_BYTES} bytes, got ${seed.byteLength}`,
    );
  }
  // Copy into a fresh ArrayBuffer-backed Uint8Array. Under TS 5.7+ DOM types,
  // `Uint8Array<ArrayBufferLike>` (which may be SharedArrayBuffer-backed)
  // can't be passed directly to `crypto.subtle.digest` (`BufferSource`).
  const data = new Uint8Array(seed);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const view = new DataView(hashBuffer);
  const emoji1 = emojiAt(view.getUint8(0));
  const emoji2 = emojiAt(view.getUint8(1));
  // big-endian 32-bit unsigned int from h[2..5]
  const num = view.getUint32(2, false);
  const digits = String(num % 10000).padStart(4, "0");
  return `${emoji1} ${emoji2} ${digits}`;
}

function emojiAt(byte: number): string {
  const emoji = FINGERPRINT_EMOJIS[byte % FINGERPRINT_EMOJIS.length];
  if (emoji === undefined) {
    // Unreachable: the table is a non-empty const list (length asserted in tests).
    throw new Error("FINGERPRINT_EMOJIS table is empty");
  }
  return emoji;
}
